"use strict";

import "whatwg-fetch";
import { optionsGen } from "../helpers/api";
import { toESQuery, toESGroupByQuery, toESDateHistogramGroupByQuery } from "../helpers/query";
import { toTitleCase } from "../helpers/converters";
import { API_URL } from "../config";
import { isArray } from "lodash";

export const ES_URL = `${API_URL}/cases/cafc-ln,cfc-ln,dc-ln,itc-ln,sc-ln/_search`;

export function esqCases(query, highlight = false) {
  const { cause_of_action, ...rest } = query;
  const q = {
    cause_of_action: cause_of_action ? (isArray(cause_of_action) ? cause_of_action.map(e => `"${e}"`) : `"${cause_of_action}"`) : cause_of_action, // do exact match for cause_of_action
    ...rest
  };
  if (!q.sort) {
    q.sort = "-filed_date";
  }
  return toESQuery(q, ["dockets"], 20, highlight);
}

export function getCases(query) {
  return fetch(ES_URL, optionsGen("POST", esqCases(query))).then(resp => resp.json());
}

export function getCase(caseNum, courtName) {
  return fetch(`${API_URL}/cases/${courtName}/${caseNum}`, optionsGen("GET", false, false))
    .then(resp => resp.json())
    .then(json => json._source);
}

export function getCaseCount(queries) {
  return fetch(ES_URL, optionsGen("POST", toESQuery(queries, [], 0)))
    .then(resp => resp.json())
    .then(json => json.hits.total.value);
}

export function getDistinctValues(field, val, query = {}) {
  return fetch(
    ES_URL,
    optionsGen(
      "POST",
      toESGroupByQuery(field, {
        ...query,
        [field]: val
      })
    )
  )
    .then(resp => resp.json())
    .then(json => json.aggregations[field].buckets.map(e => toTitleCase(e.key)));
}

function getCasesByField(queries, field, labelMap, mergeThreshold = 0.02) {
  return fetch(ES_URL, optionsGen("POST", toESGroupByQuery(field, queries, 50)))
    .then(resp => resp.json())
    .then(json => {
      const total = json.hits.total.value;
      let otherCount = json.aggregations[field].sum_other_doc_count || 0;
      let res = {};
      let keyCountPairs = json.aggregations[field].buckets;
      keyCountPairs.forEach(e => {
        const key = toTitleCase(e.key);
        const label = labelMap ? labelMap[key] || key : key;
        // put fractions less than `mergeThreshold` into `Other`
        if (e.doc_count / total >= mergeThreshold) {
          res[label] = e.doc_count;
        } else {
          otherCount += e.doc_count;
        }
      });
      if (otherCount > 0) {
        res.Other = otherCount;
      }
      return res;
    });
}

export function getCasesByStatus(queries) {
  return getCasesByField(queries, "status");
}

export function getCasesByCourt(queries) {
  return getCasesByField(queries, "court");
}

export function getCasesByEntityType(queries) {
  const ENTITY_TYPE_MAP = {
    "NPE (individual)": "NPE (Individual)",
    "NPE (patent Assertion Entity)": "NPE (Patent Assertion Entity)",
    "NPE (small Company)": "NPE (Small Company)",
    "Gov/ngo/non-Profit": "Gov/NGO/Non-Profit"
  };
  return getCasesByField(queries, "entity_type", ENTITY_TYPE_MAP, 0);
}

export function getCasesByIndustry(queries) {
  const INDUSTRY_MAP = {
    T: "High-Tech (T)",
    M: "Medical (M)",
    O: "Other (O)"
  };
  return getCasesByField(queries, "industry", INDUSTRY_MAP, 0);
}

export function getCasesByFieldOverYears(queries, field, labelMap) {
  const labelUnk = labelMap ? labelMap["UNKNOWN"] || "UNKNOWN" : "UNKNOWN";
  return fetch(ES_URL, optionsGen("POST", toESDateHistogramGroupByQuery("filed_date", field, "year", queries)))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      json.aggregations["filed_date"].buckets.forEach(b => {
        const bk = b.key_as_string.substring(0, 4);
        res[bk] = { [labelUnk]: b.doc_count };
        b[field].buckets.forEach(bib => {
          const key = bib.key_as_string ? bib.key_as_string.toUpperCase() : bib.key.toUpperCase();
          const label = labelMap ? labelMap[key] || key : key;
          res[bk][label] = bib.doc_count;
          res[bk][labelUnk] = res[bk][labelUnk] - bib.doc_count;
        });
      });
      return res;
    });
}

export function getCasesBySourceOverYears(queries) {
  const SOURCE_MAP = {
    DC: "DISTRICT COURT",
    SC: "SUPREME COURT",
    CAFC: "FEDERAL CIRCUIT",
    CFC: "FEDERAL CLAIMS",
    ITC: "INTERNATIONAL TRADE COMMISSION"
  };
  return getCasesByFieldOverYears(queries, "flag", SOURCE_MAP);
}

export function getCasesByEntityTypeOverYears(queries) {
  return getCasesByFieldOverYears(queries, "entity_type");
}

export function getCasesByTPLFOverYears(queries) {
  const LABEL_MAP = {
    TRUE: "3rd Party Financed Cases",
    FALSE: "Other NPE Cases",
    UNKNOWN: "Non-NPE Cases"
  };
  return getCasesByFieldOverYears(queries, "has_tplf", LABEL_MAP);
}
